import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import api from "../api";
import { Pencil, User, LogOut, Eye, GraduationCap, Shield, UserPlus, BookOpen, DollarSign, BarChart2, MessageSquare, Filter, Database, ChevronDown, ChevronUp, ChevronRight, FileText, Plus, Inbox, HeadphonesIcon } from "lucide-react";

import puentesLogo from "../assets/img/puentes-logo.png";
import utnLogo from "../assets/img/utn-logo.png";

// Importación de todos tus componentes
import DailyMenu from "./DailyMenu";
import CreateDiplomaturaMenu from "./CreateDiplomaturaMenu";
import DiplomaturasListModal from "./DiplomaturasListModal";
import CreateMateriaMenu from "./CreateMateriaMenu";
import StudentManagement from "./StudentManagement";
import EnrollmentModal from "./EnrollmentModal";
import AuthorityEnrollmentModal from "./AuthorityEnrollmentModal";
import MyEnrollmentsModal from "./MyEnrollmentsModal";
import PermissionManagement from "./PermissionManagement";
import SalarySettings from "./SalarySettings";
import BackupModal from "./BackupModal";
import ClassReportsModal from "./ClassReportsModal";
import GradeManagement from "./GradeManagement";
import EnrollmentManagementModal from "./EnrollmentManagementModal";
import CAUserManagement from "./CAUserManagement";
import MessageDashboard from "./MessageDashboard";
import MessageModal from "./MessageModal";
import CertificateRequestManager from "./CertificateRequestManager";
import ErrorBoundary from "./ErrorBoundary";
import RegularStudentCertificateModal from "./RegularStudentCertificateModal";
import CustomCertificateModal from "./CustomCertificateModal";
import SupportDashboard from "./SupportDashboard";

// Fuera del componente: FullCalendar trata `plugins` como configuración
// estructural. Un array nuevo en cada render (como sería un literal inline)
// hace que FullCalendar reconstruya el calendario entero en vez de solo
// re-renderizar, lo que puede pisarse con un commit de React en curso.
const FULLCALENDAR_PLUGINS = [dayGridPlugin, timeGridPlugin, interactionPlugin];

const CalendarView = ({
  impersonatedRole,
  setImpersonatedRole,
  userRole,
  showActions,
  toggleActions,
  openActionsMenu,
}) => {
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [classes, setClasses] = useState([]);

  // Estados de visibilidad de modales
  const [showDailyMenu, setShowDailyMenu] = useState(false);
  const [showCreateDiplo, setShowCreateDiplo] = useState(false);
  const [showDiplomaturasList, setShowDiplomaturasList] = useState(false);
  const [showCreateMateria, setShowCreateMateria] = useState(false);
  const [showStudentManagement, setShowStudentManagement] = useState(false);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [showMyEnrollments, setShowMyEnrollments] = useState(false);
  const [showPermissionManagement, setShowPermissionManagement] =
    useState(false);
  const [showSalarySettings, setShowSalarySettings] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showClassReports, setShowClassReports] = useState(false);
  const [showGrades, setShowGrades] = useState(false);
  const [showEnrollmentManagement, setShowEnrollmentManagement] = useState(false);
  const [showCAUserManagement, setShowCAUserManagement] = useState(false);
  const [showAuthorityEnrollment, setShowAuthorityEnrollment] = useState(false);
  const [showRegularStudentCertificate, setShowRegularStudentCertificate] = useState(false);
  const [showCustomCertificate, setShowCustomCertificate] = useState(false);
  const [showMsgSubmenu, setShowMsgSubmenu] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showMessageDashboard, setShowMessageDashboard] = useState(false);
  const [showCertificateManager, setShowCertificateManager] = useState(false);
  const [pendingCertCount, setPendingCertCount] = useState(0);
  const [approvedCertCount, setApprovedCertCount] = useState(0);
  const [certBannerDismissed, setCertBannerDismissed] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [diplomas, setDiplomas] = useState([]);
  const [selectedDiploma, setSelectedDiploma] = useState("");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showDiploSubmenu, setShowDiploSubmenu] = useState(false);
  const [showCDUsuariosSubmenu, setShowCDUsuariosSubmenu] = useState(false);
  const [showACOUsuariosSubmenu, setShowACOUsuariosSubmenu] = useState(false);
  const [showACOProfesoresSubmenu, setShowACOProfesoresSubmenu] = useState(false);
  const [showACOOtrosSubmenu, setShowACOOtrosSubmenu] = useState(false);
  const [showSupportDashboard, setShowSupportDashboard] = useState(false);

  const activeRole = impersonatedRole || userRole;
  const userEmail = sessionStorage.getItem('userEmail');

  const prevRoleRef = useRef(null);
  useEffect(() => {
    const prev = prevRoleRef.current;
    const changes = {};
    if (!prev) {
      changes._mount = true;
    } else {
      if (prev.userRole !== userRole) changes.userRole = `"${prev.userRole}" → "${userRole}"`;
      if (prev.impersonatedRole !== impersonatedRole) changes.impersonatedRole = `"${prev.impersonatedRole}" → "${impersonatedRole}"`;
      if (prev.activeRole !== activeRole) changes.activeRole = `"${prev.activeRole}" → "${activeRole}"`;
    }
    console.log("CalendarView: State updated", { changes, current: { userRole, impersonatedRole, activeRole } });
    prevRoleRef.current = { userRole, impersonatedRole, activeRole };
  }, [userRole, impersonatedRole, activeRole]);

  const fetchClasses = async () => {
    let loadedClasses = [];
    let loadedMessages = [];

    // 1. Fetch Classes (Independent)
    try {
      const res = await api.get("classes/");
      if (Array.isArray(res.data)) {
        loadedClasses = res.data.map((cls) => {
          const isPresencial = cls.modality === "PR";
          const color = isPresencial ? "#ff6b00" : "#3b82f6";
          // Si la clase termina a la medianoche (o cruza a la madrugada),
          // end_time <= start_time en el mismo `date` — hay que correr la
          // fecha de fin al día siguiente para no armar un rango negativo.
          let endDate = cls.date;
          if (cls.end_time <= cls.start_time) {
            const d = new Date(`${cls.date}T00:00:00`);
            d.setDate(d.getDate() + 1);
            endDate = d.toISOString().slice(0, 10);
          }
          return {
            id: `class-${cls.id}`,
            title: `${cls.subject_name} (${cls.diploma_name})`,
            start: `${cls.date}T${cls.start_time}`,
            end: `${endDate}T${cls.end_time}`,
            backgroundColor: color,
            borderColor: color,
            textColor: "#ffffff",
            extendedProps: { ...cls, isClass: true },
          };
        });
      }
    } catch (err) {
      console.error("Error fetching classes for calendar:", err);
    }

    // 2. Fetch Messages (Independent)
    try {
      const messagesRes = await api.get("messages/");
      if (Array.isArray(messagesRes.data)) {
        loadedMessages = messagesRes.data.map((msg) => ({
          id: `msg-${msg.id}`,
          title: msg.recipient_type === 'evento' ? `🚩 ${msg.subject}` : `✉ ${msg.subject}`,
          start: msg.date,
          allDay: true,
          backgroundColor: msg.recipient_type === 'evento' ? "#3b82f6" : "#10b981",
          borderColor: msg.recipient_type === 'evento' ? "#3b82f6" : "#10b981",
          textColor: "#ffffff",
          extendedProps: { ...msg, isMessage: true, msgData: msg },
        }));

        const userEmail = localStorage.getItem('userEmail');
        const hasUnread = messagesRes.data.some(m => !m.is_read && m.recipient_id === userEmail);
        setHasUnreadMessages(hasUnread);
      }
    } catch (err) {
      console.error("Error fetching messages for calendar:", err);
    }

    // 3. Fetch Certificate Requests (Pending count for staff, Approved count for AL)
    const activeRole = impersonatedRole || userRole;
    if (["ACO", "CD", "CG", "CO", "RM"].includes(activeRole)) {
      try {
        const certRes = await api.get("certificate-requests/");
        const pending = certRes.data.filter(r => r.status === 'PE' || r.status === 'VC').length;
        setPendingCertCount(pending);
      } catch (err) {
        console.error("Error fetching cert count:", err);
      }
    }
    if (activeRole === 'AL') {
      try {
        const certRes = await api.get("certificate-requests/");
        const approved = certRes.data.filter(r => r.status === 'AP' && r.certificate_file).length;
        setApprovedCertCount(approved);
        setCertBannerDismissed(false);
      } catch (err) {
        console.error("Error fetching AL cert count:", err);
      }
    }

    // 4. Set combined data
    setClasses([...loadedClasses, ...loadedMessages]);
  };

  useEffect(() => {
    fetchClasses();
  }, [activeRole]);

  const handleDateClick = useCallback((arg) => {
    setSelectedDate(arg.date);
    setSelectedEvent(null);
    setShowDailyMenu(true);
  }, []);
  const handleEventClick = useCallback((arg) => {
    const isMessage = arg.event.extendedProps.isMessage;
    const isClass = arg.event.extendedProps.isClass;
    if (isClass || isMessage) {
      setSelectedEvent(arg.event);
      setSelectedDate(arg.event.start);
      setShowDailyMenu(true);
    }
  }, []);

  // Memoizado: FullCalendar monta el contenido de cada evento con su propio
  // mecanismo de inyección de DOM, separado del árbol de React. Si esta función
  // cambia de referencia en cada render de CalendarView (que ocurre seguido, por
  // los toggles de menús/modales), FullCalendar redibuja toda la grilla de
  // eventos, lo que puede pisarse con otro commit de React en curso y producir
  // "insertBefore: node is not a child" en la reconciliación.
  // Memoizado por la misma razón: un array nuevo en cada render hace que
  // FullCalendar reprocese/redibuje los eventos aunque el contenido sea igual.
  const filteredEvents = useMemo(() => classes.filter((event) => {
    const isClass = event.extendedProps?.isClass;
    const eventDiplo = event.extendedProps?.diploma;
    if (selectedDiploma && isClass && eventDiplo != selectedDiploma) return false;

    if (activeRole === "CO" || activeRole === "ACO") return true;
    if (event.extendedProps?.isMessage) {
      const msg = event.extendedProps.msgData;
      if (userEmail && msg.sender === userEmail) return true;
      if (msg.recipient_type === "individual" && userEmail && msg.recipient_id === userEmail) return true;
      if (msg.recipient_type === "materia") {
        if (activeRole === "AL") return true;
        return ["CD", "PR", "DO", "CA", "CO", "ACO"].includes(activeRole);
      }
      return false;
    }
    return true;
  }), [classes, selectedDiploma, activeRole, userEmail]);

  const renderEventContent = useCallback((eventInfo) => {
    const isClass = eventInfo.event.extendedProps?.isClass;
    const title = eventInfo.event.title;

    if (isClass) {
      const subjectName = eventInfo.event.extendedProps.subject_name || title;
      const startTime = eventInfo.event.extendedProps.start_time?.substring(0, 5) || "";
      const endTime = eventInfo.event.extendedProps.end_time?.substring(0, 5) || "";

      return (
        <div className="fc-event-main-custom d-flex flex-column p-1 overflow-hidden" style={{ lineHeight: '1.2' }}>
          <div className="fc-event-title fw-bold text-truncate" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
            {subjectName}
          </div>
          <div className="fc-event-time text-truncate" style={{ fontSize: '0.65rem', opacity: 0.9 }}>
            {startTime} - {endTime}
          </div>
        </div>
      );
    }

    return (
      <div className="fc-event-main-custom p-1 text-truncate" style={{ fontSize: '0.75rem' }}>
        {title}
      </div>
    );
  }, []);

  useEffect(() => {
    if (!activeRole) return;
    let cancelled = false;
    const params = activeRole === 'AL' ? '?enrolled=true' : '';
    api.get(`diplomas/${params}`).then(res => {
      if (!cancelled) setDiplomas(res.data);
    }).catch(err => {
      if (!cancelled) console.error("Error fetching diplomas:", err);
    });
    return () => { cancelled = true; };
  }, [activeRole]);

  const handleDayMenuClose = (updated) => {
    setShowDailyMenu(false);
    setSelectedEvent(null);
    if (updated) fetchClasses();
  };

  const roleLabels = {
    CG: "Coordinadora General",
    CO: "Coordinador Operativo",
    ACO: "Auxiliar de la Coordinadora Operativa",
    CD: "Coordinador de Diplomatura",
    RM: "Referente Municipal",
    DO: "Docente",
    AL: "Alumno",
    CA: "Coordinadora Académica",
  };

  const roleHierarchy = ["CG", "CO", "ACO", "CD", "RM", "DO", "AL"];

  return (
    <ErrorBoundary>
    <div className={`main-content-area animate-fade-in h-100 ${impersonatedRole ? "impersonation-active" : ""}`}>
      <div className="calendar-container p-3">
        <header className="calendar-header-premium mb-4 glassmorphism p-4">
          <div className="institutional-header d-flex justify-content-between align-items-center flex-wrap gap-4">
            <div className="logo-section d-flex align-items-center gap-3">
              <img src={puentesLogo} alt="Logo Puentes" className="header-logo-img" />
              <div className="logo-text-group">
                <span className="logo-badge-pba">PROVINCIA DE BUENOS AIRES</span>
                <h1 className="logo-main-title text-uppercase">PUENTES <span className="fw-light">PLAN DE INTEGRACIÓN TERRITORIAL UNIVERSITARIA</span></h1>
              </div>
            </div>
            
            <div className="logo-section d-flex align-items-center gap-3 text-end justify-content-end flex-grow-1">
              <div className="logo-text-group">
                <h1 className="logo-main-title text-uppercase">UNIVERSIDAD TECNOLÓGICA NACIONAL</h1>
                <span className="logo-badge-utn">FACULTAD REGIONAL</span>
              </div>
              <img src={utnLogo} alt="Logo UTN" className="header-logo-img" />
            </div>
          </div>

          <div className="header-divider my-4"></div>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <h2 className="section-title mb-0">
                {impersonatedRole
                  ? `Vista: ${roleLabels[impersonatedRole]}`
                  : "Calendario de Eventos"}
              </h2>
              {/* FILTRO PRINCIPAL (BOTÓN PROMINENTE) - Solo visible para roles que no sean AL */}
              {activeRole !== "AL" && (
                <div className="position-relative">
                  <button
                    onClick={() => setShowFilterMenu(!showFilterMenu)}
                    className="btn-premium-action btn-light border shadow-sm"
                    title="Filtrar por Diplomatura"
                    id="diplomatura-filter-button"
                  >
                    <Filter size={18} className="me-2 text-primary" />
                    <span className="fw-bold">
                      {selectedDiploma 
                        ? (diplomas.find(d => d.id == selectedDiploma)?.name || "Filtrar") 
                        : "Filtrar por Diplomatura"}
                    </span>
                  </button>
                  <div
                    className={`actions-dropdown actions-dropdown-opaque ${showFilterMenu ? "show" : ""}`}
                    style={{ top: '110%', left: 0, minWidth: '280px', zIndex: 10002 }}
                  >
                    <button 
                      onClick={() => {
                        setSelectedDiploma("");
                        setShowFilterMenu(false);
                      }} 
                      className={`dropdown-item ${selectedDiploma === "" ? "bg-light fw-bold text-primary" : ""}`}
                    >
                      Todas las Diplomaturas
                    </button>
                    <div className="dropdown-divider"></div>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {diplomas.map(d => (
                        <button 
                          key={d.id} 
                          onClick={() => {
                            setSelectedDiploma(d.id);
                            setShowFilterMenu(false);
                          }} 
                          className={`dropdown-item ${selectedDiploma == d.id ? "bg-light fw-bold text-primary" : ""}`}
                        >
                          {d.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="actions-group gap-2">
              <div className="position-relative">
                <button
                  onClick={toggleActions}
                  className="btn-premium-action btn-primary"
                  title="Menú de Gestión"
                >
                  <Pencil size={18} className="me-2" /> Menú de Gestión
                </button>
                <div
                  className={`actions-dropdown actions-dropdown-opaque ${showActions ? "show" : ""}`}
                  style={{ top: '110%', right: 0, left: 'auto' }}
                >
                  <div className="menu-mobile-header" style={{
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem 1.25rem',
                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                    color: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}>
                    <span style={{ fontWeight: 700, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Pencil size={18} /> Menú de Gestión
                    </span>
                    <button
                      onClick={toggleActions}
                      style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.4rem', lineHeight: 1, padding: '0.2rem 0.4rem', cursor: 'pointer' }}
                      aria-label="Cerrar menú"
                    >✕</button>
                  </div>
                  <button onClick={openActionsMenu} className="dropdown-item">
                    <User size={16} className="me-2" /> Datos Personales
                  </button>

                  {["CG", "CO", "ACO", "CD", "RM", "DO", "PR", "AL"].includes(activeRole) && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDiploSubmenu(!showDiploSubmenu);
                        }}
                        className="dropdown-item d-flex align-items-center justify-content-between"
                      >
                        <div className="d-flex align-items-center">
                          <GraduationCap size={16} className="me-2 text-primary" /> Diplomaturas
                          {activeRole === 'AL' && approvedCertCount > 0 && (
                            <span className="badge bg-success rounded-pill x-small px-2 ms-2">{approvedCertCount}</span>
                          )}
                        </div>
                        {showDiploSubmenu ? <ChevronUp size={14} /> : <ChevronRight size={14} />}
                      </button>

                      {showDiploSubmenu && (
                        <div className="bg-light border-start border-3 border-primary ms-2 animate-fade-in">
                          <button
                            onClick={() => { setShowDiplomaturasList(true); toggleActions(); }}
                            className="dropdown-item small ps-4 d-flex align-items-center justify-content-between"
                          >
                            <div className="d-flex align-items-center"><Eye size={14} className="me-2" /> Ver Diplomaturas</div>
                            {activeRole === 'AL' && approvedCertCount > 0 && (
                              <span className="badge bg-success rounded-pill x-small px-2">¡Lista!</span>
                            )}
                          </button>
                          {["CG", "CO", "ACO", "CD"].includes(activeRole) && (
                            <button
                              onClick={() => { setShowCreateDiplo(true); toggleActions(); }}
                              className="dropdown-item small ps-4"
                            >
                              <Plus size={14} className="me-2 text-success" /> Crear Diplomatura
                            </button>
                          )}
                          {activeRole === "AL" && (
                            <>
                              <button onClick={() => { setShowEnrollment(true); toggleActions(); }} className="dropdown-item small ps-4">
                                <BookOpen size={14} className="me-2 text-primary" /> Inscribirse a Diplomatura
                              </button>
                              <button onClick={() => { setShowMyEnrollments(true); toggleActions(); }} className="dropdown-item small ps-4">
                                <BookOpen size={14} className="me-2 text-info" /> Mis Inscripciones
                              </button>
                            </>
                          )}
                          {activeRole === "CD" && (
                            <>
                              <button onClick={() => { setShowCertificateManager(true); toggleActions(); }} className="dropdown-item small ps-4 d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center"><FileText size={14} className="me-2 text-success" /> Gestionar Constancias</div>
                                {pendingCertCount > 0 && <span className="badge bg-danger rounded-pill x-small px-2">{pendingCertCount}</span>}
                              </button>
                              <button onClick={() => { setShowAuthorityEnrollment(true); toggleActions(); }} className="dropdown-item small ps-4">
                                <UserPlus size={14} className="me-2 text-primary" /> Inscribir Alumno
                              </button>
                              <button onClick={() => { setShowCreateMateria(true); toggleActions(); }} className="dropdown-item small ps-4">
                                <BookOpen size={14} className="me-2 text-warning" /> Materias
                              </button>
                              <button onClick={() => { setShowClassReports(true); toggleActions(); }} className="dropdown-item small ps-4">
                                <BarChart2 size={14} className="me-2 text-primary" /> Reportes de Clases
                              </button>
                            </>
                          )}
                          {activeRole === "ACO" && (
                            <>
                              <button onClick={() => { setShowCertificateManager(true); toggleActions(); }} className="dropdown-item small ps-4 d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center"><FileText size={14} className="me-2 text-success" /> Gestionar Constancias</div>
                                {pendingCertCount > 0 && <span className="badge bg-danger rounded-pill x-small px-2">{pendingCertCount}</span>}
                              </button>
                              <button onClick={() => { setShowClassReports(true); toggleActions(); }} className="dropdown-item small ps-4">
                                <BarChart2 size={14} className="me-2 text-primary" /> Reportes de Clases
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {["PR", "DO"].includes(activeRole) && (
                    <button onClick={() => { setShowGrades(true); toggleActions(); }} className="dropdown-item">
                      <GraduationCap size={16} className="me-2 text-success" /> Cargar Calificaciones
                    </button>
                  )}

{activeRole === "CO" && (
                    <button onClick={() => { setShowPermissionManagement(true); toggleActions(); }} className="dropdown-item">
                      <Shield size={16} className="me-2 text-primary" /> Reconocimiento
                    </button>
                  )}

                  {["CG", "CO", "RM"].includes(activeRole) && (
                    <button
                      onClick={() => { setShowCertificateManager(true); toggleActions(); }}
                      className="dropdown-item d-flex align-items-center justify-content-between"
                    >
                      <div className="d-flex align-items-center">
                        <FileText size={16} className="me-2 text-success" /> {activeRole === 'RM' ? 'Avalar Constancias' : 'Gestionar Constancias'}
                      </div>
                      {pendingCertCount > 0 && <span className="badge bg-danger rounded-pill x-small px-2">{pendingCertCount}</span>}
                    </button>
                  )}

                  {activeRole === "CD" && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowCDUsuariosSubmenu(!showCDUsuariosSubmenu); }}
                        className="dropdown-item d-flex align-items-center justify-content-between"
                      >
                        <div className="d-flex align-items-center">
                          <UserPlus size={16} className="me-2 text-info" /> Usuarios
                        </div>
                        {showCDUsuariosSubmenu ? <ChevronUp size={14} /> : <ChevronRight size={14} />}
                      </button>
                      {showCDUsuariosSubmenu && (
                        <div className="bg-light border-start border-3 border-info ms-2 animate-fade-in">
                          <button onClick={() => { setShowPermissionManagement(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <Shield size={14} className="me-2 text-primary" /> Reconocimiento
                          </button>
                          <button onClick={() => { setShowStudentManagement(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <UserPlus size={14} className="me-2 text-info" /> Gestión de Usuarios
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {activeRole === "CO" && (
                    <button onClick={() => { setShowSalarySettings(true); toggleActions(); }} className="dropdown-item">
                      <DollarSign size={16} className="me-2 text-success" /> Salarios y Honorarios
                    </button>
                  )}

                  {(activeRole === "CO" || localStorage.getItem('userEmail') === "mauriciocasals90@gmail.com") && (
                    <button onClick={() => { setShowBackupModal(true); toggleActions(); }} className="dropdown-item">
                      <Database size={16} className="me-2 text-warning" /> Generar Backup
                    </button>
                  )}

                  {["CO", "CA", "CG"].includes(activeRole) && (
                    <>
                      <button onClick={() => { setShowCAUserManagement(true); toggleActions(); }} className="dropdown-item">
                        <Shield size={16} className={`me-2 ${activeRole === 'CG' ? 'text-warning' : 'text-primary'}`} /> Gestión de Usuarios
                      </button>
                      {activeRole === "CA" && (
                        <>
                          <button onClick={() => { setShowAuthorityEnrollment(true); toggleActions(); }} className="dropdown-item">
                            <UserPlus size={16} className="me-2 text-primary" /> Inscribir Alumno
                          </button>
                          <button onClick={() => { setShowRegularStudentCertificate(true); toggleActions(); }} className="dropdown-item">
                            <FileText size={16} className="me-2 text-success" /> Constancia Alumno Regular
                          </button>
                          <button onClick={() => { setShowCustomCertificate(true); toggleActions(); }} className="dropdown-item">
                            <FileText size={16} className="me-2 text-primary" /> Constancia Personalizada
                          </button>
                        </>
                      )}
                      {activeRole === "CO" && (
                        <button onClick={() => { setShowEnrollmentManagement(true); toggleActions(); }} className="dropdown-item">
                          <UserPlus size={16} className="me-2 text-primary" /> Inscripciones Pendientes
                        </button>
                      )}
                    </>
                  )}

                  {activeRole === "ACO" && (
                    <>
                      {/* Usuarios */}
                      <button onClick={(e) => { e.stopPropagation(); setShowACOUsuariosSubmenu(v => !v); }} className="dropdown-item d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center"><UserPlus size={16} className="me-2 text-info" /> Usuarios</div>
                        {showACOUsuariosSubmenu ? <ChevronUp size={14} /> : <ChevronRight size={14} />}
                      </button>
                      {showACOUsuariosSubmenu && (
                        <div className="bg-light border-start border-3 border-info ms-2 animate-fade-in">
                          <button onClick={() => { setShowPermissionManagement(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <Shield size={14} className="me-2 text-primary" /> Reconocimiento
                          </button>
                          <button onClick={() => { setShowCAUserManagement(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <Shield size={14} className="me-2 text-primary" /> Gestión de Usuarios
                          </button>
                          <button onClick={() => { setShowAuthorityEnrollment(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <UserPlus size={14} className="me-2 text-primary" /> Inscribir Alumno
                          </button>
                        </div>
                      )}
                      {/* Profesores */}
                      <button onClick={(e) => { e.stopPropagation(); setShowACOProfesoresSubmenu(v => !v); }} className="dropdown-item d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center"><GraduationCap size={16} className="me-2 text-success" /> Profesores</div>
                        {showACOProfesoresSubmenu ? <ChevronUp size={14} /> : <ChevronRight size={14} />}
                      </button>
                      {showACOProfesoresSubmenu && (
                        <div className="bg-light border-start border-3 border-success ms-2 animate-fade-in">
                          <button onClick={() => { setShowSalarySettings(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <DollarSign size={14} className="me-2 text-success" /> Salarios y Honorarios
                          </button>
                        </div>
                      )}
                      {/* Otros */}
                      <button onClick={(e) => { e.stopPropagation(); setShowACOOtrosSubmenu(v => !v); }} className="dropdown-item d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center"><Database size={16} className="me-2 text-warning" /> Otros</div>
                        {showACOOtrosSubmenu ? <ChevronUp size={14} /> : <ChevronRight size={14} />}
                      </button>
                      {showACOOtrosSubmenu && (
                        <div className="bg-light border-start border-3 border-warning ms-2 animate-fade-in">
                          <button onClick={() => { setShowBackupModal(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <Database size={14} className="me-2 text-warning" /> Generar Backup
                          </button>
                          <button onClick={() => { setShowRegularStudentCertificate(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <FileText size={14} className="me-2 text-success" /> Constancia Alumno Regular
                          </button>
                          <button onClick={() => { setShowCustomCertificate(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <FileText size={14} className="me-2 text-primary" /> Constancia Personalizada
                          </button>
                          <button onClick={() => { setShowEnrollmentManagement(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <UserPlus size={14} className="me-2 text-primary" /> Inscripciones Pendientes
                          </button>
                          <button onClick={() => { setShowSupportDashboard(true); toggleActions(); }} className="dropdown-item small ps-4">
                            <HeadphonesIcon size={14} className="me-2 text-warning" /> Soporte Técnico
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {["CG", "CO", "ACO", "CD", "CA", "RM", "DO", "PR", "AL"].includes(activeRole) && (
                    <>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMsgSubmenu(!showMsgSubmenu);
                        }} 
                        className="dropdown-item d-flex align-items-center justify-content-between"
                      >
                        <div className="d-flex align-items-center">
                          <MessageSquare size={16} className="me-2 text-primary" /> Mensajes
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {hasUnreadMessages && <span className="badge bg-primary rounded-circle p-1" style={{ width: '8px', height: '8px' }}> </span>}
                          {showMsgSubmenu ? <ChevronUp size={14} /> : <ChevronRight size={14} />}
                        </div>
                      </button>

                      {showMsgSubmenu && (
                        <div className="bg-light border-start border-3 border-primary ms-2 animate-fade-in">
                          <button 
                            onClick={() => { setShowMessageDashboard(true); toggleActions(); setHasUnreadMessages(false); }} 
                            className="dropdown-item small ps-4 d-flex align-items-center justify-content-between"
                          >
                            <div className="d-flex align-items-center">
                              <Inbox size={14} className="me-2" /> Ver Mensajes
                            </div>
                          </button>
                          <button 
                            onClick={() => { setShowMessageModal(true); toggleActions(); }} 
                            className="dropdown-item small ps-4"
                          >
                            <Plus size={14} className="me-2 text-success" /> Nuevo Mensaje
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {['DO', 'PR', 'CA', 'CG', 'ACG', 'CO'].includes(activeRole) && (
                    <button onClick={() => { setShowClassReports(true); toggleActions(); }} className="dropdown-item">
                      <BarChart2 size={16} className="me-2 text-primary" /> Reportes de Clases
                    </button>
                  )}

                  <div className="dropdown-divider"></div>
                  <button onClick={() => { sessionStorage.clear(); window.location.reload(); }} className="dropdown-item text-danger">
                    <LogOut size={16} className="me-2" /> Salir
                  </button>
                </div>
              </div>

              {["CO", "ACO", "CD"].includes(userRole) && (
                <div className="position-relative">
                  <button onClick={() => setShowRoleMenu(!showRoleMenu)} className={`btn-premium-action ${impersonatedRole ? "btn-danger" : "btn-dark"}`}>
                    <Eye size={18} className="me-2" /> {impersonatedRole ? `Rol: ${roleLabels[impersonatedRole]}` : "Testear Rol"}
                  </button>
                  <div className={`actions-dropdown actions-dropdown-opaque ${showRoleMenu ? "show" : ""}`} style={{ right: 0, top: '110%' }}>
                    {roleHierarchy
                      .filter(r => r !== "CA" && roleHierarchy.indexOf(r) > roleHierarchy.indexOf(userRole))
                      .map((r) => (
                        <button key={r} onClick={async () => {
                          try {
                            await api.post('users/impersonate/', { role: r });
                            setImpersonatedRole(r);
                            setShowRoleMenu(false);
                          } catch (err) {
                            console.error("Error setting impersonation:", err);
                            alert("No se pudo cambiar el rol de prueba.");
                          }
                        }} className="dropdown-item">
                          {roleLabels[r]}
                        </button>
                    ))}
                    <div className="dropdown-divider"></div>
                    <button onClick={async () => {
                      try {
                        await api.post('users/impersonate/', { role: "" });
                        setImpersonatedRole("");
                        setShowRoleMenu(false);
                      } catch (err) {
                        console.error("Error clearing impersonation:", err);
                      }
                    }} className="dropdown-item text-primary">
                      Restablecer mi Rol
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {activeRole === 'AL' && approvedCertCount > 0 && !certBannerDismissed && (
          <div className="alert alert-success d-flex align-items-center justify-content-between mb-3 shadow-sm" role="alert">
            <div className="d-flex align-items-center gap-2">
              <FileText size={18} className="text-success flex-shrink-0" />
              <span>
                <strong>¡Tu constancia de alumno regular está lista!</strong> Entrá a <strong>Menú de Gestión → Diplomaturas → Ver Diplomaturas</strong> para descargarla.
              </span>
            </div>
            <button
              type="button"
              className="btn-close ms-3 flex-shrink-0"
              aria-label="Cerrar"
              onClick={() => setCertBannerDismissed(true)}
            />
          </div>
        )}

        <FullCalendar
          plugins={FULLCALENDAR_PLUGINS}
          initialView="dayGridMonth"
          locale={esLocale}
          events={filteredEvents}
          eventContent={renderEventContent}
          height="85vh"
          dateClick={handleDateClick}
          eventClick={handleEventClick}
        />

        {/* MODALES */}
        {showGrades && <GradeManagement onClose={() => setShowGrades(false)} role={activeRole} />}
        {showDailyMenu && <DailyMenu date={selectedDate} role={activeRole} eventToEdit={selectedEvent} onClose={handleDayMenuClose} />}
        {showCreateDiplo && <CreateDiplomaturaMenu onClose={() => setShowCreateDiplo(false)} />}
        {showDiplomaturasList && <DiplomaturasListModal onClose={() => setShowDiplomaturasList(false)} activeRole={activeRole} />}
        {showCreateMateria && <CreateMateriaMenu onClose={() => setShowCreateMateria(false)} />}
        {showStudentManagement && <StudentManagement onClose={() => setShowStudentManagement(false)} />}
        {showSalarySettings && <SalarySettings onClose={() => setShowSalarySettings(false)} userRole={userRole} />}
        {showBackupModal && <BackupModal onClose={() => setShowBackupModal(false)} />}
        {showEnrollment && <EnrollmentModal onClose={() => { setShowEnrollment(false); fetchClasses(); }} />}
        {showMyEnrollments && <MyEnrollmentsModal onClose={() => setShowMyEnrollments(false)} onUpdate={fetchClasses} />}
        {showPermissionManagement && <PermissionManagement onClose={() => setShowPermissionManagement(false)} activeRole={impersonatedRole || userRole} />}
        {showCertificateManager && (
          <ErrorBoundary>
            <CertificateRequestManager onClose={() => setShowCertificateManager(false)} activeRole={activeRole} />
          </ErrorBoundary>
        )}
        {showMessageModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 11000 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content glassmorphism p-4 border-0">
                <MessageModal 
                  date={new Date()} 
                  onClose={(refresh) => { 
                    setShowMessageModal(false); 
                    if (refresh === true) fetchClasses();
                  }} 
                />
              </div>
            </div>
          </div>
        )}
        {showClassReports && <ClassReportsModal onClose={() => setShowClassReports(false)} role={activeRole} userEmail={localStorage.getItem("userEmail")} />}
        {showEnrollmentManagement && <EnrollmentManagementModal onClose={() => setShowEnrollmentManagement(false)} />}
        {showCAUserManagement && <CAUserManagement onClose={() => setShowCAUserManagement(false)} userRole={userRole} />}
        {showAuthorityEnrollment && <AuthorityEnrollmentModal isOpen={showAuthorityEnrollment} onClose={() => setShowAuthorityEnrollment(false)} onEnrollSuccess={fetchClasses} />}
        {showRegularStudentCertificate && <RegularStudentCertificateModal onClose={() => setShowRegularStudentCertificate(false)} />}
        {showCustomCertificate && <CustomCertificateModal onClose={() => setShowCustomCertificate(false)} />}
        {showMessageDashboard && <MessageDashboard onClose={() => setShowMessageDashboard(false)} userRole={activeRole} />}
        {showSupportDashboard && <SupportDashboard onClose={() => setShowSupportDashboard(false)} />}
      </div>
    </div>
    </ErrorBoundary>
  );
};

export default CalendarView;
